import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { setAuth } from '../Components/Redux/authSlice';
import { RootState } from '../Components/Redux/store';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import printJS from 'print-js';
import 'bootstrap/dist/css/bootstrap.min.css';
import './AttendanceReport.css';

const AttendanceViewer: React.FC = () => {
  const [classes, setClasses] = useState<any[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState<number | null>(null);
  const [selectedSection, setSelectedSection] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [attendanceData, setAttendanceData] = useState<any>(null);
  const [attendanceRecords, setAttendanceRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth.value);

  // Utility function to get cookie value by name
  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i].trim();
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length);
      }
    }
    return '';
  };

  // Check user authentication on mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        await axios.get('/users/user');
        dispatch(setAuth(true));
      } catch (e) {
        dispatch(setAuth(false));
      }
    };
    fetchUserData();
  }, [dispatch]);

  // Fetch classes on mount
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await axios.get('/Branches/classes/');
        setClasses(response.data);
      } catch (error) {
        console.error('Error fetching classes:', error);
        setError('Failed to fetch classes.');
      }
    };
    fetchClasses();
  }, []);

  // Fetch sections for the selected class using "class_id"
  const fetchSectionsByClass = async (classId: number) => {
    try {
      const response = await axios.get(`/Branches/sections/?class_id=${classId}`);
      setSections(response.data);
    } catch (error) {
      console.error('Error fetching sections:', error);
      setSections([]);
      setError('Failed to fetch sections.');
    }
  };

  // Fetch attendance records using the selected filters
  const fetchAttendanceRecords = async (classId: number, sectionId: number, date: string) => {
    setLoading(true);
    setError(null);
    try {
      const token = getCookie('token');
      const response = await axios.get('/Branches/attendance/', {
        params: {
          class_id: classId,
          section_id: sectionId,
          date: date,
        },
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      setAttendanceData(response.data);
      setAttendanceRecords(response.data.attendance_records || []);
    } catch (error) {
      console.error('Error fetching attendance records:', error);
      setError('Failed to load attendance records.');
      setAttendanceRecords([]);
      setAttendanceData(null);
    } finally {
      setLoading(false);
    }
  };

  // Event handlers for filter changes
  const handleClassChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === "") {
      setSelectedClass(null);
      setSections([]);
      setSelectedSection(null);
      setAttendanceRecords([]);
      setAttendanceData(null);
      return;
    }
    const classId = Number(value);
    setSelectedClass(classId);
    setSelectedSection(null);
    setAttendanceRecords([]);
    setAttendanceData(null);
    setSelectedDate('');
    await fetchSectionsByClass(classId);
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === "") {
      setSelectedSection(null);
      setAttendanceRecords([]);
      setAttendanceData(null);
      return;
    }
    const sectionId = Number(value);
    setSelectedSection(sectionId);
    if (selectedClass && sectionId && selectedDate) {
      fetchAttendanceRecords(selectedClass, sectionId, selectedDate);
    } else {
      setAttendanceRecords([]);
      setAttendanceData(null);
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value;
    setSelectedDate(date);
    if (selectedClass && selectedSection && date) {
      fetchAttendanceRecords(selectedClass, selectedSection, date);
    } else {
      setAttendanceRecords([]);
      setAttendanceData(null);
    }
  };

  // Export attendance records to Excel
  const exportToExcel = () => {
    if (attendanceRecords.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(
      attendanceRecords.map((record) => ({
        Taariikh: record.date,
        Ardayga: record.student_name,
        Xaaladda: record.status,
        Faahfaahin: record.remarks,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Diiwaanka Xaadirinta');
    XLSX.writeFile(wb, 'diiwaanka_xaadirinta.xlsx');
  };

  // Export attendance records to PDF
  const exportToPDF = () => {
    if (attendanceRecords.length === 0) return;
    const doc = new jsPDF();
    doc.text('Diiwaanka Xaadirinta', 14, 16);
    doc.autoTable({
      head: [['Taariikh', 'Ardayga', 'Xaaladda', 'Faahfaahin']],
      body: attendanceRecords.map((record) => [
        record.date,
        record.student_name,
        record.status,
        record.remarks,
      ]),
      startY: 20,
    });
    doc.save('diiwaanka_xaadirinta.pdf');
  };

  // Export absent students to Excel
  const exportAbsentStudentsToExcel = () => {
    if (!attendanceData || !attendanceData.absent_students) return;
    const ws = XLSX.utils.json_to_sheet(
      attendanceData.absent_students.map((student: any) => ({
        Magaca: student.name,
        Fasalka: student.class,
        Qeybta: student.section,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ardayda Maqan');
    XLSX.writeFile(wb, 'ardayda_maqan.xlsx');
  };

  // Export absent students to PDF
  const exportAbsentStudentsToPDF = () => {
    if (!attendanceData || !attendanceData.absent_students) return;
    const doc = new jsPDF();
    doc.text('Ardayda Maqan', 14, 16);
    doc.autoTable({
      head: [['Magaca', 'Fasalka', 'Qeybta']],
      body: attendanceData.absent_students.map((student: any) => [
        student.name,
        student.class,
        student.section,
      ]),
      startY: 20,
    });
    doc.save('ardayda_maqan.pdf');
  };

  // Download the full JSON report as a file
  const downloadFullReport = () => {
    if (!attendanceData) return;
    const blob = new Blob([JSON.stringify(attendanceData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `attendance_report_${selectedDate || 'report'}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Print the attendance records table using printJS
  const printRecords = () => {
    const table = document.querySelector('.attendance-table');
    if (table) {
      printJS({ printable: table, type: 'html', header: 'Diiwaanka Xaadirinta' });
    }
  };

  return (
    <div className="attendance-report-container">
      {/* Hero Header */}
      <div className="report-hero">
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="icon">📅</span> Warbixinta Xaadirinta
          </h1>
          <p className="hero-subtitle">La soco oo falanqee qaabka xaadirinta ardayda</p>
        </div>
      </div>

      {auth ? (
        <>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0">Warbixinta Xaadirinta</h2>
            {attendanceRecords.length > 0 && (
              <div className="d-flex">
                <button className="btn btn-outline-primary me-2" onClick={exportToExcel}>
                  <i className="bi bi-file-earmark-excel me-1"></i> Excel
                </button>
                <button className="btn btn-outline-secondary me-2" onClick={exportToPDF}>
                  <i className="bi bi-file-earmark-pdf me-1"></i> PDF
                </button>
                <button className="btn btn-outline-info me-2" onClick={printRecords}>
                  <i className="bi bi-printer me-1"></i> Daabac
                </button>
                <button className="btn btn-outline-success" onClick={downloadFullReport}>
                  <i className="bi bi-download me-1"></i> JSON
                </button>
              </div>
            )}
          </div>

          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <h5 className="card-title mb-4">Shaandhee Diiwaanka Xaadirinta</h5>
              <div className="row g-3">
                <div className="col-md-4">
                  <label htmlFor="classSelect" className="form-label fw-semibold">Fasalka</label>
                  <select
                    id="classSelect"
                    onChange={handleClassChange}
                    className="form-select"
                  >
                    <option value="">-- Dooro Fasal --</option>
                    {classes.map((cls) => (
                      <option key={cls.id} value={cls.id}>
                        {cls.name}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedClass && (
                  <div className="col-md-4">
                    <label htmlFor="sectionSelect" className="form-label fw-semibold">Qeybta</label>
                    <select
                      id="sectionSelect"
                      onChange={handleSectionChange}
                      className="form-select"
                    >
                      <option value="">-- Dooro Qeyb --</option>
                      {sections.map((section) => (
                        <option key={section.id} value={section.id}>
                          {section.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {selectedClass && selectedSection && (
                  <div className="col-md-4">
                    <label htmlFor="dateSelect" className="form-label fw-semibold">Taariikhda</label>
                    <input
                      type="date"
                      id="dateSelect"
                      value={selectedDate}
                      onChange={handleDateChange}
                      className="form-control"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="d-flex justify-content-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Soo dejinaya...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          ) : attendanceRecords.length > 0 ? (
            <>
              <div className="card shadow-sm mb-4">
                <div className="card-header bg-light">
                  <h5 className="mb-0">Diiwaanka Xaadirinta</h5>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0 attendance-table">
                      <thead className="table-light">
                        <tr>
                          <th scope="col">Taariikhda</th>
                          <th scope="col">Ardayga</th>
                          <th scope="col">Xaaladda</th>
                          <th scope="col">Faahfaahin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attendanceRecords.map((record, index) => (
                          <tr key={index}>
                            <td>{record.date}</td>
                            <td className="fw-semibold">{record.student_name}</td>
                            <td>
                              <span className={`badge ${record.status === 'Present' ? 'bg-success' : 'bg-danger'}`}>
                                {record.status === 'Present' ? 'Jooga' : 'Maqan'}
                              </span>
                            </td>
                            <td>{record.remarks}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {attendanceData && attendanceData.absent_students && attendanceData.absent_students.length > 0 && (
                <div className="card shadow-sm">
                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Ardayda Maqan</h5>
                    <div>
                      <button className="btn btn-sm btn-outline-success me-2" onClick={exportAbsentStudentsToExcel}>
                        <i className="bi bi-file-earmark-excel me-1"></i> Excel
                      </button>
                      <button className="btn btn-sm btn-outline-secondary" onClick={exportAbsentStudentsToPDF}>
                        <i className="bi bi-file-earmark-pdf me-1"></i> PDF
                      </button>
                    </div>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th scope="col">Magaca</th>
                            <th scope="col">Fasalka</th>
                            <th scope="col">Qeybta</th>
                          </tr>
                        </thead>
                        <tbody>
                          {attendanceData.absent_students.map((student: any, index: number) => (
                            <tr key={index}>
                              <td className="fw-semibold">{student.name}</td>
                              <td>{student.class}</td>
                              <td>{student.section}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            selectedClass && selectedSection && selectedDate && (
              <div className="card shadow-sm">
                <div className="card-body text-center py-5">
                  <i className="bi bi-calendar-check display-4 text-muted"></i>
                  <p className="mt-3 mb-0 text-muted">
                    Lama helin diiwaan xaadirin oo u dhiganta shaandhaynta la doortay.
                  </p>
                </div>
              </div>
            )
          )}
        </>
      ) : (
        <div className="card shadow-sm">
          <div className="card-body text-center py-5">
            <i className="bi bi-shield-lock display-4 text-muted"></i>
            <p className="mt-3 mb-0 text-muted">
              Fadlan soo gal si aad u aragto warbixinta xaadirinta.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceViewer;